import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cave-hero-salon/artifacts/mz0y0jq3_WhatsApp_Image_2026-02-20_at_5.57.50_PM-removebg-preview.png";

const NAV_LINKS = [
  { path: "/", label: "Home" },
  { path: "/about", label: "About" },
  { path: "/services", label: "Services" },
  { path: "/blog", label: "Blog" },
  { path: "/franchise", label: "Franchise" },
  { path: "/book", label: "Book" },
  { path: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
    };
  }, [mobileOpen]);

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#0D0D0D]/90 backdrop-blur-md py-3 border-b border-white/5"
            : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <img src={LOGO_URL} alt="CAVE Men's Salon" className="h-11 w-auto object-contain" />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`nav-link text-xs uppercase tracking-[0.2em] font-medium transition-colors ${
                  location.pathname === link.path
                    ? "text-[#D4AF37] active"
                    : "text-[#A3A3A3] hover:text-[#E5E5E5]"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="tel:+917022116556"
              data-testid="nav-call-btn"
              className="flex items-center gap-2 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.15em] font-semibold px-5 py-2.5 rounded-sm hover:bg-[#E5C585] transition-all duration-300 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]"
            >
              <Phone className="w-3.5 h-3.5" />
              Call Now
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-[#E5E5E5] p-2 relative z-[70]"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Overlay - separate from nav, covers full viewport */}
      {mobileOpen && (
        <div
          data-testid="mobile-overlay"
          className="fixed inset-0 z-[55] bg-black/80 backdrop-blur-[10px] md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Menu Panel - sits above overlay with solid background */}
      <div
        data-testid="mobile-menu"
        className={`fixed inset-0 z-[60] md:hidden flex flex-col items-center justify-center gap-8 bg-[#0A0A0A]/95 transition-all duration-300 ${
          mobileOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {NAV_LINKS.map((link, i) => (
          <Link
            key={link.path}
            to={link.path}
            onClick={() => setMobileOpen(false)}
            data-testid={`mobile-nav-${link.label.toLowerCase()}`}
            className={`text-2xl font-sans font-semibold tracking-wider transition-all duration-300 ${
              mobileOpen ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
            } ${
              location.pathname === link.path
                ? "text-[#D4AF37]"
                : "text-[#E5E5E5] hover:text-[#D4AF37]"
            }`}
            style={{ transitionDelay: mobileOpen ? `${i * 50}ms` : "0ms" }}
          >
            {link.label}
          </Link>
        ))}
        <a
          href="tel:+917022116556"
          className={`mt-4 flex items-center gap-2 bg-[#D4AF37] text-black text-sm uppercase tracking-[0.15em] font-semibold px-6 py-3 rounded-sm transition-all duration-300 ${
            mobileOpen ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
          style={{ transitionDelay: mobileOpen ? `${NAV_LINKS.length * 50}ms` : "0ms" }}
        >
          <Phone className="w-4 h-4" />
          Call Now
        </a>
      </div>
    </>
  );
}
